import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { useI18n, type MessageKey } from '@/i18n';
import { HEADER_CATEGORIES } from '@/lib/categories';
import type { HubCategory } from '@/types';

interface MobileMenuProps {
  categories: HubCategory[];
  onCategoryChange?: (categoryId: string) => void;
}

const SECTION_LINKS: Array<{ to: string; key: MessageKey }> = [
  { to: '/roadmap', key: 'nav.roadmap' },
  { to: '/assets', key: 'nav.assets' },
];

export function MobileMenu({ categories, onCategoryChange }: MobileMenuProps) {
  const { email, signOut } = useAuth();
  const { t } = useI18n();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const currentCategory = new URLSearchParams(location.search).get('category');
  const onLibrary = location.pathname.startsWith('/library');

  function itemClass(active: boolean) {
    return `nav-dropdown__item${active ? ' is-active' : ''}`;
  }

  return (
    <div className="top-nav__menu" ref={rootRef}>
      <button
        type="button"
        className="lang-switch__trigger top-nav__menu-trigger"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {t('nav.menu')}
      </button>

      {open ? (
        <div className="top-nav__menu-panel glass-panel">
          <Link
            to="/"
            className={itemClass(location.pathname === '/')}
            onClick={() => setOpen(false)}
          >
            {t('nav.home')}
          </Link>

          {HEADER_CATEGORIES.map((item) => {
            const meta = categories.find((cat) => cat.id === item.id);
            /* A category the hub does not serve yet is announced, not linked. */
            if (!meta || meta.comingSoon) {
              return (
                <div key={item.id} className="nav-dropdown__item is-disabled" aria-disabled="true">
                  <span>{t(item.key)}</span>
                  <span className="nav-dropdown__soon">{t('nav.comingSoon')}</span>
                </div>
              );
            }
            return (
              <Link
                key={item.id}
                to={`/library?category=${encodeURIComponent(item.id)}`}
                className={itemClass(onLibrary && currentCategory === item.id)}
                onClick={() => {
                  onCategoryChange?.(item.id);
                  setOpen(false);
                }}
              >
                {t(item.key)}
              </Link>
            );
          })}

          <span className="top-nav__menu-divider" aria-hidden="true" />

          {SECTION_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={itemClass(location.pathname.startsWith(link.to))}
              onClick={() => setOpen(false)}
            >
              {t(link.key)}
            </Link>
          ))}

          <span className="top-nav__menu-divider" aria-hidden="true" />

          <Link
            to="/account"
            className={itemClass(location.pathname.startsWith('/account'))}
            onClick={() => setOpen(false)}
          >
            {t('nav.account')}
          </Link>
          <button
            type="button"
            className="nav-dropdown__item top-nav__menu-signout"
            onClick={() => {
              setOpen(false);
              signOut();
            }}
          >
            {t('nav.signOut')}
          </button>
          {email ? <p className="account-menu__email">{email}</p> : null}
        </div>
      ) : null}
    </div>
  );
}
